import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Clock, Mail, Phone, FileText, User, MapPin, Briefcase } from 'lucide-react';
import Header from "../../components/Public/ui/Header";
import Footer from "../../components/Public/ui/Footer";
import DemoToolWidget from "../../components/DemoToolWidget";
import NeedAssistanceBanner from "../../components/common/NeedAssistanceBanner";

export default function StatusUnderVerification() {
    const location = useLocation();
    const navigate = useNavigate();
    const {
        message,
        applicationNumber: initialAppNumber,
        applicationData: initialAppData,
        userData: initialUserData
    } = location.state || {};

    const [applicationNumber, setApplicationNumber] = useState(initialAppNumber);
    const [applicationData, setApplicationData] = useState(initialAppData);
    const [userData, setUserData] = useState(initialUserData);
    const [isLoading, setIsLoading] = useState(!initialAppNumber);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/user`, {
                    credentials: "include",
                });
                const result = await res.json();
                const isPendingState = ["under_verification", "under_renewal", "pending"].includes(result.status);
                if (result.success && !isPendingState) {
                    navigate("/passport/application", { replace: true });
                    return;
                }
                if (result.success && result.applicationData) {
                    setApplicationNumber(result.applicationNumber || result.applicationData.application_number);
                    setApplicationData(result.applicationData);
                    setUserData(result.userData);
                }
            } catch (e) { console.error(e); } finally { setIsLoading(false); }
        };
        fetchStatus();
    }, [navigate]);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
            <Header />

            <main className="flex-grow flex items-center justify-center p-4 sm:p-6"
                style={{
                    backgroundImage: "radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(241,245,249,1) 100%), url('/assets/Images/SLUDI-background.png')",
                    backgroundSize: "cover",
                    backgroundAttachment: "fixed"
                }}>

                <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

                    {/* Updated Status Header - Removed Yellow */}
                    <div className="bg-slate-50 border-b border-slate-200 p-6 sm:px-8">
                        <div className="flex items-center gap-5">
                            {/* Icon Background made White */}
                            <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-200">
                                <Clock className="w-6 h-6 text-slate-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 leading-tight">
                                    {applicationData?.status === 'under_renewal' ? 'Renewal under review' : 'Under verification'}
                                </h2>
                                <p className="text-slate-500 text-sm mt-0.5">
                                    {message || "We are currently processing your request. Completion usually takes 3-5 business days."}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 sm:p-8 space-y-8">

                        {/* Application Overview */}
                        <section>
                            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <FileText size={14} /> Application overview
                            </h3>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-6">
                                <SummaryItem label="Ref number" value={applicationNumber} isMono />
                                <SummaryItem label="Service" value={applicationData?.service_type} />
                                <SummaryItem label="Travel doc" value={applicationData?.travel_document_type} />
                                <SummaryItem label="Submitted" value={applicationData?.submitted_at ? new Date(applicationData.submitted_at).toLocaleDateString() : '—'} />
                            </div>
                        </section>

                        {/* Your Information */}
                        <section className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <User size={14} /> Your information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
                                <DataField label="Full name" value={userData?.name || `${applicationData?.other_names} ${applicationData?.surname}`} />
                                <DataField label="sludi number" value={userData?.sludiNumber || applicationData?.sludi_number} />
                                <DataField label="Profession" value={applicationData?.profession} />
                                <DataField label="District" value={applicationData?.district} />
                                <DataField label="Email address" value={userData?.email} />
                                <DataField label="Mobile number" value={userData?.phone || applicationData?.mobile_number} />
                            </div>
                        </section>

                        {/* Updated Banner to "info" to avoid yellow "warning" styles */}
                        <NeedAssistanceBanner
                            type="info"
                            message="Contact us if you find any errors in your information."
                        />
                    </div>
                </div>
            </main>

            <Footer />
            <DemoToolWidget context="pending" applicationData={applicationData} />
        </div>
    );
}

function SummaryItem({ label, value, isMono = false }) {
    return (
        <div className="flex flex-col">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">{label}</span>
            <span className={`text-slate-800 font-bold leading-tight ${isMono ? 'font-mono text-sm' : 'text-[15px]'}`}>
                {value || '—'}
            </span>
        </div>
    );
}

function DataField({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex items-center py-1.5 border-b border-slate-200/50 last:border-0 gap-4">
            <span className="text-[11px] font-medium text-slate-500 uppercase w-32 shrink-0">{label}</span>
            <span className="text-slate-800 font-semibold text-sm">{value}</span>
        </div>
    );
}